(ns manage.content-variant-page.placeholder.db
  "One-way synchronization of the content variant data model with the content configuration data model"
  (:require [clojure.string :as str]
            [datascript.core :as d]
            [manage.content-configuration-page.placeholder-expression.db :refer [singular-placeholder-expression]]
            [manage.content-configuration-page.placeholder.db :as placeholder]
            [manage.content-configuration-page.predicate.db :as predicate]
            [manage.content-variant-page.content-configuration.db :refer [replace-invalid-chars]]
            [manage.content-variant-page.rule.db :as rule]
            [re-frame.core :as rf]
            [re-posh.core :as rp]))

(defn targeting-key->placeholder-name
  ([targeting-key-name]
   (replace-invalid-chars targeting-key-name))
  ([ds tk]
   (-> (d/pull ds [:variant-feed-key/name] tk)
       :variant-feed-key/name
       targeting-key->placeholder-name)))

(defn find-placeholder-by-name [ds placeholder-name]
  (d/q '[:find ?p . :in $ ?cc ?n
         :where
         [?p :placeholder/name ?n]
         [?cc :content-configuration/placeholder ?p]]
       ds placeholder/root-container placeholder-name))

(defn sync-targeting-key-placeholder [ds tk]
  (let [placeholder-name (targeting-key->placeholder-name ds tk)
        placeholder (or (d/q '[:find ?p . :in $ ?tk
                               :where [?tk :targeting-key/placeholder ?p]]
                             ds tk)
                        (find-placeholder-by-name ds placeholder-name))]
    (when placeholder
      {:placeholder/name placeholder-name
       :targeting-key/_placeholder tk
       :db/id placeholder})))

(rp/reg-event-ds :variant-feed/sync-targeting-keys->placeholders
  ;; Link targeting keys with existing placeholders. Do not delete placeholders
  ;; for which the targeting key does not exist anymore. These will become
  ;; 'dangling placeholders'. Also, do not create missing placeholders.
  (fn [ds [_ vf]]
    (keep (partial sync-targeting-key-placeholder ds)
          (d/q '[:find [?tk ...]
                 :in $ ?vf
                 :where
                 [?vf :variant-feed/variant-feed-key ?tk]
                 [?tk :variant-feed-key/type :targeting]] ds vf))))

(rp/reg-query-sub :placeholder/targeting-key
  '[:find ?tk .
    :in $ ?p
    :where
    [?tk :targeting-key/placeholder ?p]])

(rf/reg-event-fx :placeholder/configure
  [(rf/inject-cofx :sub (fn [[_ p]] [:ui.store/value p :placeholder/name]))]
  (fn [{placeholder-name :ui.store/value} _]
    {:fx [[:dispatch [:entity/view {:entity :placeholder :name placeholder-name}]]]}))

(rf/reg-event-fx :targeting-key/create-placeholder-success
  [(rf/inject-cofx :sub (fn [[_ tk]] [:ui.store/value tk :targeting-key/placeholder]))]
  (fn [{p :ui.store/value} _]
    {:fx [[:dispatch [:placeholder/configure p]]]}))

(rf/reg-event-fx :targeting-key/configure
  [(rf/inject-cofx :ds)]
  (fn [{:keys [ds]} [_ tk]]
    {:fx [[:posh/transact!
           [(let [placeholder-name (targeting-key->placeholder-name ds tk)]
              (concat
               (placeholder/db-create-placeholder placeholder/root-container placeholder-name :text)
               (rule/sync-dynamic-targeting-key-rule ds tk [])
               [{:db/id tk :targeting-key/placeholder (placeholder/tempid placeholder/root-container placeholder-name)}]))
            {:on-success [:targeting-key/create-placeholder-success tk]}]]]}))

(rf/reg-sub :placeholder/rules
  (fn [[_ p]]
    [(rf/subscribe [:ui.store/pull-entity '[{:content-configuration/rule [:rule/name]}] placeholder/root-container])
     (rf/subscribe [:ui.store/value p :placeholder/name])])
  (fn [[{rules :content-configuration/rule} placeholder-name] _]
    (filter (fn [{rule-name :rule/name}]
              (or (= rule-name (str placeholder-name rule/dynamic-targeting-rule-suffix))
                  (str/starts-with? rule-name (str placeholder-name rule/mapped-targeting-rule-value-separator))))
            rules)))

(rf/reg-sub :placeholder/rules-eids
  (fn [[_ p]]
    (rf/subscribe [:placeholder/rules p]))
  (fn [rules _]
    (map :db/id rules)))

(defn dynamic?
  "returns false if there is a rule with the same name as the Placeholder but not
  ending with '-LEMONPI_WILDCARD'. In all other cases it will return true.

  Assumes all rules in input have a name starting with the placeholder name"
  [rules]
  (not-any? (fn [{rule-name :rule/name}]
              (not (str/ends-with? rule-name rule/dynamic-targeting-rule-suffix)))
            rules))

(rf/reg-sub :placeholder/dynamic?
  (fn [[_ p]] (rf/subscribe [:placeholder/rules p]))
  (fn [rules _] (dynamic? rules)))

(rp/reg-sub :placeholder/macros
  (fn [[_ p]] (rf/subscribe [:placeholder/rules p]))
  (fn [rules [_ p]]
    (if (dynamic? rules)
      {:type :query
       :query '[:find [?m ...] :in $ ?p
                :where
                [?p :placeholder/override ?phe]
                [?phe :singular-placeholder-expression/macro ?m]]
       :variables [p]}
      {:type :query
       :query '[:find [?m ...] :in $ [?r ...]
                :where [?r :rule/condition ?m]]
       :variables [(map :db/id rules)]})))

(rp/reg-sub :placeholder/full-macro-entities
  (fn [[_ p]] (rf/subscribe [:placeholder/macros p]))
  (fn [macros _]
    {:type :pull-many
     :pattern [:macro/selector {:context-source/_macro [:context-source/alias :context-source/schema]}]
     :ids macros}))

(rf/reg-sub :placeholder/unique-macros
  (fn [[_ p]] (rf/subscribe [:placeholder/full-macro-entities p]))
  (fn [macros _]
    (group-by
     (fn [macro]
       {:macro/selector (:macro/selector macro)
        :context-source/_macro [:context-source/alias (get-in macro [:context-source/_macro :context-source/alias])]})
     macros)))

(defn default-targeting-value [ds p]
  (d/q '[:find [?tv ?value] :in $ ?p
         :where
         [?p :placeholder/default-value ?phe]
         [?tv :targeting-value/placeholder-expression ?phe]
         [?tv :targeting-value/value ?value]] ds p))

(defn mapped-targeting-rule [db ds p macros tv]
  (let [mapped-rule (rule/sync-mapped-targeting-value-rule ds tv true (= tv (first (default-targeting-value ds p))))]
    (concat mapped-rule (predicate/create-many db ds [(:db/id (first mapped-rule))] macros))))

(defn ->mapped [db ds p dynamic-rule macros]
  (into [[:db/retractEntity dynamic-rule]]
        (mapcat (partial mapped-targeting-rule db ds p macros))
        (d/q '[:find [?tv ...] :in $ ?p
               :where
               [?tk :targeting-key/placeholder ?p]
               [?tk :targeting-key/targeting-value ?tv]] ds p)))

(rf/reg-event-fx :placeholder/->mapped
  [(rf/inject-cofx :ds)
   (rf/inject-cofx :sub (fn [[_ p]] [:placeholder/unique-macros p]))
   (rf/inject-cofx :sub (fn [[_ p]] [:placeholder/rules-eids p]))]
  (fn [{:keys [db ds] :placeholder/keys [unique-macros rules-eids]} [_ p]]
    {:fx [[:transact (->mapped db ds p (first rules-eids) (keys unique-macros))]]}))

(rf/reg-event-fx :placeholder/add-macro
  [(rf/inject-cofx :ds)
   (rf/inject-cofx :sub (fn [[_ p _macro]] [:placeholder/dynamic? p]))
   (rf/inject-cofx :sub (fn [[_ p _macro]] [:placeholder/rules-eids p]))
   (rf/inject-cofx :sub (fn [[_ p _macro]] [:placeholder/unique-macros p]))]
  (fn [{:keys [db ds] :placeholder/keys [dynamic? rules-eids unique-macros]} [_ p macro]]
    {:fx [[:transact
           (cond
             ;; if this is a dynamic placeholder and there is already exactly one macro,
             ;; then first switch to mapped targeting and then add the new macro
             (and dynamic? (= (count unique-macros) 1))
             (->mapped db ds p (first rules-eids) (conj (keys unique-macros) macro))

             (and dynamic? (zero? (count unique-macros)))
             (rule/sync-dynamic-targeting-key-rule-assignments ds (first rules-eids) p [macro])

             (not dynamic?)
             (predicate/create-many db ds rules-eids [macro]))]]}))

(rf/reg-event-fx :placeholder/->dynamic
  [(rf/inject-cofx :ds)
   (rf/inject-cofx :sub (fn [[_ p]] [:placeholder/unique-macros p]))
   (rf/inject-cofx :sub (fn [[_ p]] [:placeholder/targeting-key p]))
   (rf/inject-cofx :sub (fn [[_ p]] [:placeholder/rules-eids p]))]
  (fn [{:keys [ds] :placeholder/keys [unique-macros targeting-key rules-eids]} [_ p]]
    {:fx [[:transact (concat (rule/sync-dynamic-targeting-key-rule ds targeting-key (keys unique-macros))
                             (when-let [[tv value] (default-targeting-value ds p)]
                               [(assoc (singular-placeholder-expression [value] [] false)
                                       :placeholder/_default-value p
                                       :targeting-value/_placeholder-expression tv)])
                             (map (partial vector :db/retractEntity) rules-eids))]]}))

(rf/reg-sub :placeholder/allow-toggle-dynamic<->mapped?
  (fn [[_ p]]
    [(rf/subscribe [:resource.content-function-version/active-version? "published"])
     (rf/subscribe [:placeholder/unique-macros p])])
  (fn [[is-published-version macros] _]
    (and (not is-published-version)
         (= (count macros) 1))))

(rf/reg-sub :placeholder/allow-delete?
  (fn [[_ p]]
    (rf/subscribe [:placeholder/targeting-key p]))
  (fn [targeting-key _]
    (nil? targeting-key)))
